import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
    RoleInfo,
    Vocabulary,
    deleteRole,
    fetchVocabulary,
    listRoles,
} from '../../api/authz';
import { ApiError } from '../../api/request';
import RoleEditor from './RoleEditor';
import RoleMembersModal from './RoleMembersModal';
import { confirmDialog, alertDialog } from '../../utils/dialog';

/**
 * 角色管理 (2026-08-31): 内置角色只读展示, 自定义角色可增删改.
 * 权限按「资源域 × 动作」配置, 见 RoleEditor.
 */

const READ_VERBS = ['get'];

/** 新建角色在 URL 里的占位名 (?role=__new__). */
const NEW_ROLE = '__new__';

/**
 * 权限概览: 该角色能只读多少资源域、能编辑多少。
 * 与编辑器的三档口径一致 —— 非只读动作 (写 + 运维治理) 统一算作"编辑"。
 */
function summarize(role: RoleInfo, vocab: Vocabulary | null) {
    const resources = vocab?.resources || [];
    const hit = (res: string, pick: (verb: string) => boolean) =>
        role.rules.some(
            (r) =>
                (r.resources.includes('*') || r.resources.includes(res)) &&
                (r.verbs.includes('*') || r.verbs.some(pick)),
        );
    let read = 0;
    let edit = 0;
    const sensitive: string[] = [];
    resources.forEach((r) => {
        const canRead = hit(r.key, (v) => READ_VERBS.includes(v));
        const canEdit = hit(r.key, (v) => !READ_VERBS.includes(v));
        if (canRead) read += 1;
        if (canEdit) edit += 1;
        if ((canRead || canEdit) && r.sensitive) sensitive.push(r.label);
    });
    return { read, edit, sensitive };
}

function RoleList() {
    const [list, setList] = useState<RoleInfo[]>([]);
    const [vocab, setVocab] = useState<Vocabulary | null>(null);
    const history = useHistory();
    const location = useLocation();
    // 编辑态走 URL query (?role=<角色名>, 新建为 __new__): 打开是 push, 关闭是 replace ——
    // 浏览器后退回到角色列表而不是上一个页面, 刷新/分享也不丢位 (2026-09-13).
    const editingName = new URLSearchParams(location.search).get('role') || '';
    const openEditor = (name: string) => {
        const params = new URLSearchParams(location.search);
        params.set('role', name);
        history.push({
            pathname: location.pathname,
            search: params.toString(),
        });
    };
    const closeEditor = () => {
        const params = new URLSearchParams(location.search);
        params.delete('role');
        history.replace({
            pathname: location.pathname,
            search: params.toString(),
        });
    };
    const [expanded, setExpanded] = useState<string | null>(null);
    const [members, setMembers] = useState<RoleInfo | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await listRoles();
            setList(res.list || []);
        } catch (e) {
            setError(
                e instanceof ApiError ? `${e.code}: ${e.message}` : String(e),
            );
            setList([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
        fetchVocabulary()
            .then(setVocab)
            .catch(() => setVocab(null));
    }, [load]);

    const onDelete = async (role: RoleInfo) => {
        if (
            !(await confirmDialog(
                `确认删除角色「${role.displayName}」(${role.name})?`,
            ))
        )
            return;
        try {
            await deleteRole(role.name);
            await load();
        } catch (e) {
            alertDialog(
                `删除失败: ${e instanceof ApiError ? e.message : String(e)}`,
            );
        }
    };

    if (editingName) {
        const source =
            editingName === NEW_ROLE
                ? undefined
                : list.find((r) => r.name === editingName);
        if (vocab && (editingName === NEW_ROLE || source)) {
            return (
                <RoleEditor
                    // 按角色名 remount: 直接改 URL 里的 role 在不同角色间切换时, 勾选态不能沿用上一个角色的
                    key={editingName}
                    vocab={vocab}
                    source={source}
                    onDone={() => {
                        closeEditor();
                        load();
                    }}
                    onCancel={closeEditor}
                />
            );
        }
        if (loading || !vocab) {
            return (
                <div className="py-10 text-center text-sm text-slate-500">
                    正在加载角色…
                </div>
            );
        }
        // URL 里的角色已被删 / 名字不对: 给个回列表的出口, 不留白屏
        return (
            <div className="py-10 text-center text-sm text-slate-500">
                角色「{editingName}」不存在
                <button
                    type="button"
                    className="btn-ghost ml-2"
                    onClick={closeEditor}
                >
                    返回角色列表
                </button>
            </div>
        );
    }

    const renderCard = (role: RoleInfo) => {
        const s = summarize(role, vocab);
        const open = expanded === role.name;
        return (
            <div key={role.name} className="card p-4">
                <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-slate-800">
                                {role.displayName}
                            </span>
                            {role.system && (
                                <span
                                    className="tag bg-slate-100 text-slate-500"
                                    title="由准入身份自动生效, 可改权限但不可删除; 不能绑到用户组"
                                >
                                    系统角色
                                </span>
                            )}
                        </div>
                        <div className="mt-0.5 font-mono text-xs text-slate-400">
                            {role.name}
                        </div>
                        {role.description && (
                            <div className="mt-1.5 text-xs text-slate-500">
                                {role.description}
                            </div>
                        )}
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                        <button
                            type="button"
                            className="btn-ghost"
                            title="查看 / 添加 / 移除拥有该角色的用户"
                            onClick={() => setMembers(role)}
                        >
                            成员
                            {role.bindingCount > 0
                                ? ` (${role.bindingCount})`
                                : ''}
                        </button>
                        <button
                            type="button"
                            className="btn-ghost"
                            disabled={!vocab}
                            onClick={() => openEditor(role.name)}
                        >
                            编辑
                        </button>
                        {!role.system && (
                            <button
                                type="button"
                                className="btn-danger-text"
                                onClick={() => onDelete(role)}
                            >
                                删除
                            </button>
                        )}
                    </div>
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-2">
                    <span className="tag bg-sky-50 text-sky-700">
                        只读 {s.read} 个资源域
                    </span>
                    {s.edit > 0 && (
                        <span className="tag bg-amber-50 text-amber-700">
                            可编辑 {s.edit} 个
                        </span>
                    )}
                    {s.sensitive.length > 0 ? (
                        <span
                            className="tag bg-rose-50 text-rose-600"
                            title={s.sensitive.join('、')}
                        >
                            含敏感:{s.sensitive.slice(0, 2).join('、')}
                            {s.sensitive.length > 2
                                ? ` 等 ${s.sensitive.length} 项`
                                : ''}
                        </span>
                    ) : (
                        <span className="tag bg-emerald-50 text-emerald-700">
                            不含敏感资源
                        </span>
                    )}
                    <button
                        type="button"
                        className="text-xs text-slate-400 hover:text-slate-600"
                        onClick={() => setExpanded(open ? null : role.name)}
                    >
                        {open ? '收起明细' : '查看明细'}
                    </button>
                </div>

                {open && (
                    <div className="mt-3 space-y-1 border-t border-slate-100 pt-3">
                        {role.rules.map((rule, i) => (
                            <div key={i} className="text-xs text-slate-600">
                                <span className="text-slate-400">资源</span>{' '}
                                {rule.resources.join('、')}{' '}
                                <span className="text-slate-400">可</span>{' '}
                                {rule.verbs.join('、')}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="space-y-5">
            <div className="flex items-start justify-between gap-4">
                <div className="text-xs leading-relaxed text-slate-500">
                    角色定义「能碰哪些资源、能做哪些动作」,在「权限分配」里把角色绑给人。
                    <br />
                    <strong className="text-slate-600">管理员</strong>和
                    <strong className="text-slate-600">普通用户</strong>
                    是系统角色,按准入身份自动生效 —— 权限可以改,但不能删除,
                    也不能绑到用户组(身份与准入只能对人授予)。
                </div>
                <button
                    type="button"
                    className="btn-primary"
                    disabled={!vocab}
                    onClick={() => openEditor(NEW_ROLE)}
                >
                    + 新建角色
                </button>
            </div>

            {error && (
                <div className="card border-amber-200 bg-amber-50 p-3 text-sm text-amber-700">
                    {error}
                </div>
            )}
            {loading && <div className="text-sm text-slate-400">加载中…</div>}

            <div className="space-y-2">{list.map(renderCard)}</div>

            {!loading && list.length === 0 && (
                <div className="card p-6 text-center text-sm text-slate-400">
                    还没有任何角色,点右上角「新建角色」开始配置
                </div>
            )}
            <RoleMembersModal
                role={members}
                onClose={() => setMembers(null)}
                onChanged={load}
            />
        </div>
    );
}

export default RoleList;
